import { type AuthToken } from '@reliance/types/models/Auth';
import { account } from './resources/account';
import { acknowledgement } from './resources/acknowledgement';
import { audioVisitation } from './resources/audioVisitation';
import { auth } from './resources/auth';
import { block } from './resources/block';
import { caseReports } from './resources/caseReports';
import { cardAccount } from './resources/cardAccount';
import { cdr } from './resources/cdr';
import { commissary } from './resources/commissary';
import { condition } from './resources/conditions';
import { configuration } from './resources/configuration';
import { email } from './resources/email';
import { eventNotification } from './resources/eventNotification';
import { facility } from './resources/facility';
import { facilityUser } from './resources/facilityUser';
import { housingUnit } from './resources/housingUnit';
import { inmateAccount } from './resources/inmateAccount';
import { inmateContact } from './resources/inmateContact';
import { inmateDevice } from './resources/inmateDevice';
import { inmateNotification } from './resources/inmateNotification';
import { inmatePostalMail } from './resources/inmatePostalMail';
import { inmateRequest } from './resources/inmateRequest';
import { inmates } from './resources/inmates';
import { itds } from './resources/itds';
import { location as locationResource } from './resources/location';
import { mobileDevice } from './resources/mobileDevice';
import { outsideParty } from './resources/outsideParty';
import { personSearch } from './resources/personSearch';
import { phoneCard } from './resources/phoneCard';
import { privateNumber } from './resources/privateNumber';
import { recordingBatch } from './resources/recordingBatch';
import { referenceMaterial } from './resources/referenceMaterial';
import { reports } from './resources/reports';
import { station } from './resources/station';
import { tax } from './resources/tax';
import { textMessage } from './resources/textMessage';
import { visitation } from './resources/visitation';
import { visitationPending } from './resources/visitationPending';
import { voicemail } from './resources/voicemail';
import { website } from './resources/website';
import { ai } from './resources/ai';
import ApiService from './services/api_service';
import config from './services/config';
import { type AccessToken, type QueryParams } from './types/ApiService';

const siteApi = new ApiService(config.api.url, 'site');
const centralApi = new ApiService(
  config.centralApi?.url || 'https://api.reliancetelephone.com',
  'central',
);
let accessToken: AccessToken | null = null;

const resources = {
  setToken: (token: AccessToken | null) => {
    // Keep site and central ApiService instances in sync when auth changes
    accessToken = token;
    siteApi.setToken(token);
    centralApi.setToken(token);
  },
  getToken: async (): Promise<AuthToken | null> => {
    if (siteApi.isTokenExpired()) {
      return siteApi.refreshToken();
    }
    return Promise.resolve(accessToken);
  },
  getCentralGetResource<FilterType extends QueryParams, ResponseType>(
    url: string,
  ) {
    return (params: Partial<FilterType>): Promise<ResponseType> => {
      return centralApi.get(url, { params });
    };
  },
  getSiteGetResource<FilterType extends QueryParams, ResponseType>(
    url: string,
  ) {
    return (params: Partial<FilterType>): Promise<ResponseType> => {
      return siteApi.get(url, { params });
    };
  },

  account: account(centralApi),
  acknowledgement: acknowledgement(centralApi),
  audioVisitation: audioVisitation(siteApi),
  auth: auth(siteApi),
  block: block(centralApi),
  caseReports: caseReports(centralApi, () => accessToken!),
  cardAccount: cardAccount(centralApi),
  condition: condition(centralApi),
  cdr: cdr(siteApi, centralApi, () => accessToken!),
  commissary: commissary(centralApi, () => accessToken!),
  configuration: configuration(siteApi),
  email: email(centralApi),
  eventNotification: eventNotification(centralApi),
  facility: facility(centralApi),
  facilityUser: facilityUser(centralApi),
  housingUnit: housingUnit(centralApi),
  inmateAccount: inmateAccount(siteApi, centralApi),
  inmateContact: inmateContact(siteApi, centralApi),
  inmateDevice: inmateDevice(siteApi),
  inmateNotification: inmateNotification(centralApi),
  inmatePostalMail: inmatePostalMail(centralApi),
  inmateRequest: inmateRequest(centralApi, () => accessToken!),
  inmates: inmates(centralApi, () => accessToken!),
  itds: itds(siteApi),
  location: locationResource(centralApi),
  mobileDevice: mobileDevice(siteApi),
  outsideParty: outsideParty(() => accessToken!),
  personSearch: personSearch(centralApi),
  phoneCard: phoneCard(centralApi),
  privateNumber: privateNumber(centralApi),
  recordingBatch: recordingBatch(centralApi, () => accessToken!),
  referenceMaterial: referenceMaterial(() => accessToken!),
  reports: reports(centralApi, () => accessToken!),
  station: station(siteApi),
  tax: tax(centralApi),
  textMessage: textMessage(siteApi, centralApi, () => accessToken!),
  visitation: visitation(siteApi, () => accessToken!),
  visitationPending: visitationPending(siteApi),
  voicemail: voicemail(siteApi, () => accessToken!),
  website: website(siteApi),
  ai: ai(centralApi),
};

export default resources;
